<template>
    <div class="mt-8 border-t border-slate-200 pt-5">
        <!-- Section Header -->
        <div class="mb-4">
            <h2 class="font-semibold leading-none text-slate-800">Sponsorship Analytics</h2>
            <p class="text-sm text-slate-500 mt-1">Breakdown of sponsored content performance and budget allocation.</p>
        </div>

        <div v-if="!hasSponsorshipData" class="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
            <p class="text-sm text-slate-500">No sponsored content detected in this report.</p>
        </div>

        <div v-else class="space-y-6">
            <!-- Top Row: Content Volume + Budget Allocation -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <!-- Content Volume and Platform Distribution -->
                <div ref="contentVolumeRef" class="rounded-lg border border-slate-200 bg-white shadow-sm flex flex-col">
                    <header class="border-b border-slate-100 px-4 py-3 flex items-center justify-between">
                        <div>
                            <h3 class="text-sm font-semibold text-slate-800">Content Volume</h3>
                            <p class="text-xs text-slate-500">Views by platform</p>
                        </div>
                        <ActionMenu>
                            <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportContentVolume">
                                <ImageIcon class="h-3.5 w-3.5" />
                                <span>Export as Image</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportContentVolumeCsv">
                                <AppIcon name="file-csv" class="h-3.5 w-3.5" />
                                <span>Export as CSV</span>
                            </DropdownMenuItem>
                        </ActionMenu>
                    </header>
                    <div class="flex-1 flex flex-col overflow-auto">
                        <div v-if="contentVolumeData.length === 0" class="py-4 text-center text-xs text-slate-400">
                            No content volume data available
                        </div>
                        <table v-else class="w-full text-sm flex-1">
                            <thead>
                                <tr class="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                                    <th class="text-left font-medium pb-2 px-3 pt-3">Platform</th>
                                    <th class="text-right font-medium pb-2 px-3 pt-3">Total Views</th>
                                    <th class="text-right font-medium pb-2 px-3 pt-3">Sponsored Views</th>
                                    <th class="text-right font-medium pb-2 px-3 pt-3 w-32">% Sponsored</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-slate-100">
                                <tr v-for="row in contentVolumeData" :key="row.key" class="group">
                                    <td class="py-2 px-3">
                                        <div class="flex items-center gap-2">
                                            <AppIcon
                                                :name="getPlatformIcon(row.platformId)"
                                                class="w-4 h-4"
                                                :class="getPlatformIconColor(row.platformId)"
                                            />
                                            <span class="text-slate-700">{{ row.label }}</span>
                                        </div>
                                    </td>
                                    <td class="py-2 px-3 text-right text-slate-600 tabular-nums">{{ formatNumber(row.views) }}</td>
                                    <td class="py-2 px-3 text-right text-slate-600 tabular-nums">{{ formatNumber(row.sponsoredViews) }}</td>
                                    <td class="py-2 px-3 text-right text-slate-600 tabular-nums">
                                        {{ formatPercent(row.sponsoredPercent) }}
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot class="border-t border-slate-300 bg-slate-50 sticky bottom-0">
                                <tr class="font-semibold">
                                    <td class="py-2.5 px-3 text-slate-800">Total</td>
                                    <td class="py-2.5 px-3 text-right text-slate-800 tabular-nums">{{ formatNumber(contentVolumeTotals.views) }}</td>
                                    <td class="py-2.5 px-3 text-right text-slate-800 tabular-nums">{{ formatNumber(contentVolumeTotals.sponsoredViews) }}</td>
                                    <td class="py-2.5 px-3 text-right text-slate-800 tabular-nums">
                                        {{ formatPercent(contentVolumeTotals.sponsoredPercent) }}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                <!-- Budget Allocation Breakdown -->
                <div ref="budgetAllocationRef" class="rounded-lg border border-slate-200 bg-white shadow-sm flex flex-col">
                    <header class="border-b border-slate-100 px-4 py-3 flex items-center justify-between">
                        <div>
                            <h3 class="text-sm font-semibold text-slate-800">Budget Allocation</h3>
                            <p class="text-xs text-slate-500">Est. value by platform</p>
                        </div>
                        <ActionMenu>
                            <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportBudgetAllocation">
                                <ImageIcon class="h-3.5 w-3.5" />
                                <span>Export as Image</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportBudgetAllocationCsv">
                                <AppIcon name="file-csv" class="h-3.5 w-3.5" />
                                <span>Export as CSV</span>
                            </DropdownMenuItem>
                        </ActionMenu>
                    </header>
                    <div class="flex-1 flex flex-col">
                        <!-- Header Row -->
                        <div class="flex items-center justify-between text-xs text-slate-500 uppercase tracking-wide font-medium border-b border-slate-200 px-3 pt-3 pb-2">
                            <div>Platform</div>
                            <div>Budget</div>
                        </div>
                        <div class="divide-y divide-slate-100 flex-1">
                            <div v-if="budgetAllocationData.length === 0" class="py-4 text-center text-xs text-slate-400">
                                No budget allocation data available
                            </div>
                            <div v-for="row in budgetAllocationData" :key="row.key" class="group flex gap-3 px-3 py-3">
                                <div class="flex-shrink-0 flex items-center justify-center">
                                    <AppIcon
                                        :name="getPlatformIcon(row.platformId)"
                                        class="w-6 h-6"
                                        :class="getPlatformIconColor(row.platformId)"
                                    />
                                </div>
                                <div class="flex-1 min-w-0">
                                    <div class="flex items-center justify-between text-sm mb-1">
                                        <span class="text-slate-700">{{ row.label }}</span>
                                        <span class="text-slate-800 font-medium tabular-nums">{{ formatCurrency(row.budget) }}</span>
                                    </div>
                                    <div class="h-2 bg-slate-100 rounded-full overflow-hidden">
                                        <div
                                            class="h-full rounded-full transition-all duration-300"
                                            :class="getBudgetBarColor(row.key)"
                                            :style="{ width: `${Math.min(row.budgetPercent, 100)}%` }"
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Total Row -->
                        <div class="border-t border-slate-300 bg-slate-50 px-3 py-3 rounded-b-lg mt-auto">
                            <div class="flex items-center justify-between text-sm font-semibold">
                                <span class="text-slate-800">Total</span>
                                <span class="text-slate-800 tabular-nums">{{ formatCurrency(budgetAllocationTotal) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Bottom Row: Cost Efficiency + Top Creators -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <!-- Cost Efficiency Analysis -->
                <div ref="costEfficiencyRef" class="rounded-lg border border-slate-200 bg-white shadow-sm flex flex-col">
                    <header class="border-b border-slate-100 px-4 py-3 flex items-center justify-between">
                        <div>
                            <h3 class="text-sm font-semibold text-slate-800">Cost Efficiency</h3>
                            <p class="text-xs text-slate-500">CPM by platform</p>
                        </div>
                        <ActionMenu>
                            <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportCostEfficiency">
                                <ImageIcon class="h-3.5 w-3.5" />
                                <span>Export as Image</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportCostEfficiencyCsv">
                                <AppIcon name="file-csv" class="h-3.5 w-3.5" />
                                <span>Export as CSV</span>
                            </DropdownMenuItem>
                        </ActionMenu>
                    </header>
                    <div class="overflow-auto flex-1 flex flex-col">
                        <div v-if="costEfficiencyData.length === 0" class="py-4 text-center text-xs text-slate-400">
                            No cost efficiency data available
                        </div>
                        <template v-else>
                            <table class="w-full text-sm">
                                <thead>
                                    <tr class="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                                        <th class="text-left font-medium pb-2 px-3 pt-3">Platform</th>
                                        <th class="text-right font-medium pb-2 px-3 pt-3">Budget</th>
                                        <th class="text-right font-medium pb-2 px-3 pt-3">Views</th>
                                        <th class="text-right font-medium pb-2 px-3 pt-3">CPM</th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-slate-100">
                                    <tr v-for="row in costEfficiencyData" :key="row.key">
                                        <td class="py-2 px-3">
                                            <div class="flex items-center gap-2">
                                                <AppIcon
                                                    :name="getPlatformIcon(row.platformId)"
                                                    class="w-4 h-4"
                                                    :class="getPlatformIconColor(row.platformId)"
                                                />
                                                <span class="text-slate-700">{{ row.label }}</span>
                                            </div>
                                        </td>
                                        <td class="py-2 px-3 text-right text-slate-600 tabular-nums">{{ formatCurrency(row.budget) }}</td>
                                        <td class="py-2 px-3 text-right text-slate-600 tabular-nums">{{ formatNumber(row.views) }}</td>
                                        <td class="py-2 px-3 text-right text-slate-800 font-medium tabular-nums">
                                            {{ formatCurrency(row.cpm) }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <table class="w-full text-sm mt-auto">
                                <tfoot class="border-t border-slate-300 bg-slate-50">
                                    <tr class="font-semibold">
                                        <td class="py-2.5 px-3 text-slate-800">Total</td>
                                        <td class="py-2.5 px-3 text-right text-slate-800 tabular-nums">{{ formatCurrency(costEfficiencyTotals.budget) }}</td>
                                        <td class="py-2.5 px-3 text-right text-slate-800 tabular-nums">{{ formatNumber(costEfficiencyTotals.views) }}</td>
                                        <td class="py-2.5 px-3 text-right text-slate-800 tabular-nums"></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </template>
                    </div>
                </div>

                <!-- Top Creators Hired -->
                <div ref="topCreatorsRef" class="rounded-lg border border-slate-200 bg-white shadow-sm flex flex-col">
                    <header class="border-b border-slate-100 px-4 py-3 flex items-center justify-between">
                        <div>
                            <h3 class="text-sm font-semibold text-slate-800">Top Sponsored Creators</h3>
                            <p class="text-xs text-slate-500">By estimated value</p>
                        </div>
                        <ActionMenu>
                            <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportTopCreators">
                                <ImageIcon class="h-3.5 w-3.5" />
                                <span>Export as Image</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem class="cursor-pointer gap-2 text-xs" @click="exportTopCreatorsCsv">
                                <AppIcon name="file-csv" class="h-3.5 w-3.5" />
                                <span>Export as CSV</span>
                            </DropdownMenuItem>
                        </ActionMenu>
                    </header>
                    <div class="pt-1 flex-1 flex flex-col">
                        <div v-if="isLoadingCreators" class="py-4 text-center text-xs text-slate-400">
                            Loading...
                        </div>
                        <div v-else-if="topCreatorsData.length === 0" class="py-4 text-center text-xs text-slate-400">
                            No creator-level sponsorship data available
                        </div>
                        <div v-else class="flex-1 overflow-auto">
                            <table class="w-full text-sm h-full">
                                <thead>
                                    <tr class="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                                        <th class="text-left font-medium pb-2 px-3 pt-2" style="width: 32px;"></th>
                                        <th class="text-left font-medium pb-2 px-1 pt-2" style="width: 40px;"></th>
                                        <th class="text-left font-medium pb-2 px-2 pt-2">Creator</th>
                                        <th class="text-right font-medium pb-2 px-3 pt-2" style="width: 100px;">Views</th>
                                        <th class="text-left font-medium pb-2 px-3 pt-2" style="width: 110px;">
                                            <div class="flex items-center gap-1">
                                                <Tooltip placement="top">
                                                    <template #trigger>
                                                        <AppIcon name="circle-info" class="h-3 w-3 text-slate-400 hover:text-slate-500 transition-colors cursor-help" />
                                                    </template>
                                                    <div class="text-xs">
                                                        <div class="font-semibold mb-1">Average Performance</div>
                                                        <div>Positive means above average, negative means below.</div>
                                                    </div>
                                                </Tooltip>
                                                <span>Performance</span>
                                            </div>
                                        </th>
                                        <th class="text-right font-medium pb-2 px-3 pt-2" style="width: 100px;">Est. Payout</th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-slate-100">
                                    <tr v-for="(creator, idx) in topCreatorsData" :key="creator.id">
                                        <!-- Rank Badge -->
                                        <td class="py-2 px-3">
                                            <div
                                                class="w-5 h-5 rounded-full flex items-center justify-center"
                                                :class="idx < 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'"
                                            >
                                                <span class="text-xs font-semibold">{{ idx + 1 }}</span>
                                            </div>
                                        </td>
                                        <!-- Avatar -->
                                        <td class="py-2 px-1">
                                            <div class="cursor-pointer" @click="openCreatorProfile(creator)">
                                                <img
                                                    :src="creator.avatarUrl"
                                                    :alt="creator.displayName"
                                                    class="w-8 h-8 rounded-full object-cover bg-slate-200 hover:ring-2 hover:ring-indigo-300 transition-shadow"
                                                    @error="(e) => e.target.src = getDefaultAvatar(creator)"
                                                />
                                            </div>
                                        </td>
                                        <!-- Creator Info -->
                                        <td class="py-2 px-2">
                                            <div
                                                class="text-sm font-medium text-slate-800 truncate cursor-pointer hover:text-indigo-600 transition-colors"
                                                @click="openCreatorProfile(creator)"
                                            >{{ creator.displayName }}</div>
                                            <div class="flex items-center gap-1 leading-none">
                                                <AppIcon
                                                    v-for="platformId in getPlatformOrder(creator.platformIds)"
                                                    :key="platformId"
                                                    :name="getPlatformIcon(platformId)"
                                                    class="w-3 h-3"
                                                    :class="getPlatformIconColor(platformId)"
                                                />
                                            </div>
                                        </td>
                                        <!-- Views -->
                                        <td class="py-2 px-3 text-right text-sm text-slate-600 tabular-nums">{{ formatNumber(creator.sponsoredViews) }}</td>
                                        <!-- Performance -->
                                        <td class="py-2 px-3">
                                            <span
                                                v-if="creator.performance != null"
                                                class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 font-medium tabular-nums text-xs text-white"
                                                :class="creator.performance >= 0
                                                    ? 'bg-emerald-500'
                                                    : 'bg-rose-500'"
                                            >
                                                <AppIcon
                                                    :name="creator.performance >= 0 ? 'arrow-trend-up' : 'arrow-trend-down'"
                                                    class="h-3 w-3"
                                                />
                                                {{ numeral(Math.abs(creator.performance)).format('0.0%') }}
                                            </span>
                                            <span v-else class="text-slate-400 text-sm">—</span>
                                        </td>
                                        <!-- Est. Payout -->
                                        <td class="py-2 px-3 text-right text-sm font-semibold text-slate-800 tabular-nums">{{ formatCurrency(creator.sponsoredValue) }}</td>
                                    </tr>
                                </tbody>
                                <tfoot class="border-t border-slate-300 bg-slate-50">
                                    <tr class="font-semibold">
                                        <td class="py-2.5 px-3 text-slate-800" colspan="3">Total</td>
                                        <td class="py-2.5 px-3 text-right text-slate-800 tabular-nums">{{ formatNumber(topCreatorsTotals.views) }}</td>
                                        <td class="py-2.5 px-3"></td>
                                        <td class="py-2.5 px-3 text-right text-slate-800 tabular-nums">{{ formatCurrency(topCreatorsTotals.payout) }}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { MEDIA_CDN_BASE } from '@/utils/media'
import _ from 'lodash'
import numeral from 'numeral'
import { axios } from '@/utils/axios'
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import {
    ActionMenu,
    DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import { Image as ImageIcon } from 'lucide-vue-next'
import { useCsvExport } from '@/composables/useCsvExport'
import { useImageExport } from '@/composables/useImageExport'
import Tooltip from '@/components/Tooltip.vue'
import { getPlatformOrder } from '@/utils/Utils'

const props = defineProps({
    facets: {
        type: Object,
        default: () => ({}),
    },
    reportId: {
        type: String,
        default: null,
    },
    selectedFacetDimension: {
        type: String,
        default: 'all',
    },
    selectedFacetKey: {
        type: String,
        default: 'all',
    },
})

const store = useStore()
const { downloadAsImage } = useImageExport()
const { downloadAsCsv } = useCsvExport()

// Open creator profile modal
const openCreatorProfile = (creator) => {
    if (!creator.primaryPlatformId || !creator.primaryPlatformUserId) return

    store.commit('modal:setData', {
        modal_id: 'creator',
        data: {
            is_open: true,
            platform_id: creator.primaryPlatformId,
            platform_user_id: creator.primaryPlatformUserId,
        },
    })
}

// Refs for image export
const contentVolumeRef = ref(null)
const budgetAllocationRef = ref(null)
const costEfficiencyRef = ref(null)
const topCreatorsRef = ref(null)

// Platform + content type label mapping
const platformContentTypeLabels = {
    youtube_video: 'YouTube Videos',
    youtube_short: 'YouTube Shorts',
    youtube_stream: 'YouTube Streams',
    twitch_stream: 'Twitch Streams',
    tiktok_video: 'TikTok',
    twitter_post: 'X',
    instagram_post: 'Instagram Posts',
    instagram_video: 'Instagram Videos',
    instagram_story: 'Instagram Stories',
    instagram_image: 'Instagram Images',
    instagram_carousel: 'Instagram Carousels',
}

// Extract platform ID from composite key (e.g. 'twitch_stream' -> 'twitch')
const getPlatformIdFromKey = (key) => {
    if (!key) return null
    const parts = key.split('_')
    return parts[0] || null
}

// Extract content type from composite key (e.g. 'twitch_stream' -> 'stream', 'youtube_short' -> 'short')
const getContentTypeFromKey = (key) => {
    if (!key) return null
    const parts = key.split('_')
    return parts.slice(1).join('_') || null
}

// Platform icon mapping
const getPlatformIcon = (platformId) => {
    const iconMap = {
        twitch: 'twitch',
        youtube: 'youtube',
        instagram: 'instagram',
        tiktok: 'tiktok',
        twitter: 'x-twitter',
        x: 'x-twitter',
    }
    return iconMap[platformId?.toLowerCase()] || 'globe'
}

// Platform icon color mapping
const getPlatformIconColor = (platformId) => {
    const colorMap = {
        twitch: 'text-purple-500',
        youtube: 'text-red-500',
        instagram: 'text-pink-500',
        tiktok: 'text-slate-800',
        twitter: 'text-x',
        x: 'text-slate-800',
    }
    return colorMap[platformId?.toLowerCase()] || 'text-slate-400'
}

// Platform label mapping for creators table
const platformLabels = {
    youtube: 'YouTube',
    twitch: 'Twitch',
    tiktok: 'TikTok',
    twitter: 'X',
    instagram: 'IG',
}

// Budget bar colors by platform
const budgetBarColors = {
    youtube_video: 'bg-red-400',
    youtube_short: 'bg-red-300',
    youtube_stream: 'bg-red-500',
    twitch_stream: 'bg-purple-500',
    tiktok_video: 'bg-slate-700',
    twitter_post: 'bg-x',
    instagram_post: 'bg-pink-400',
    instagram_video: 'bg-pink-500',
    instagram_story: 'bg-orange-400',
    instagram_image: 'bg-pink-300',
    instagram_carousel: 'bg-fuchsia-400',
}

// Returns a predicate to filter by_platform_and_content_type keys based on the active split selection
const contentTypeKeyFilter = computed(() => {
    const { selectedFacetDimension: dim, selectedFacetKey: key } = props
    if (dim === 'by_platform' && key && key !== 'all') {
        return (k) => getPlatformIdFromKey(k) === key
    }
    if (dim === 'by_content_type' && key && key !== 'all') {
        return (k) => getContentTypeFromKey(k) === key
    }
    return null
})

// Check if we have sponsorship data (organic filter means no sponsored content)
const hasSponsorshipData = computed(() => {
    if (props.selectedFacetDimension === 'by_sponsored' && props.selectedFacetKey === 'false') {
        return false
    }
    return props.facets?.by_sponsored?.true?.sum_content > 0
})

// Get platform+content_type facet data
const platformContentTypeFacets = computed(() => {
    const facet = props.facets?.by_platform_and_content_type
    if (!facet) return []

    const filter = contentTypeKeyFilter.value
    return Object.entries(facet)
        .filter(([key]) => !filter || filter(key))
        .map(([key, data]) => ({
            key,
            label: platformContentTypeLabels[key] || key,
            platformId: getPlatformIdFromKey(key),
            ...data,
        }))
        .filter((item) => item.sum_content > 0)
})

// Get sponsored facet nested by platform+content_type
const sponsoredByPlatformContentType = computed(() => {
    const sponsored = props.facets?.by_sponsored?.true?.facets?.by_platform_and_content_type
    if (!sponsored) return {}
    const filter = contentTypeKeyFilter.value
    if (filter) {
        return Object.fromEntries(Object.entries(sponsored).filter(([key]) => filter(key)))
    }
    return sponsored
})

// Content Volume and Platform Distribution data
const contentVolumeData = computed(() => {
    const totalViews = props.facets?.all?.all?.sum_views || 0

    return _.orderBy(
        platformContentTypeFacets.value.map((item) => {
            const sponsoredData = sponsoredByPlatformContentType.value[item.key] || {}
            const views = item.sum_views || 0
            const sponsoredViews = sponsoredData.sum_views || 0

            return {
                key: item.key,
                label: item.label,
                platformId: item.platformId,
                views,
                viewsPercent: totalViews > 0 ? (views / totalViews) * 100 : 0,
                sponsoredViews,
                sponsoredPercent: views > 0 ? (sponsoredViews / views) * 100 : 0,
            }
        }),
        ['views'],
        ['desc']
    )
})

// Content Volume totals for the footer row
const contentVolumeTotals = computed(() => {
    const data = contentVolumeData.value
    const views = data.reduce((sum, row) => sum + row.views, 0)
    const sponsoredViews = data.reduce((sum, row) => sum + row.sponsoredViews, 0)
    const sponsoredPercent = views > 0 ? (sponsoredViews / views) * 100 : 0

    return {
        views,
        sponsoredViews,
        sponsoredPercent,
    }
})

// Budget Allocation Breakdown data
const budgetAllocationData = computed(() => {
    const rows = _.orderBy(
        platformContentTypeFacets.value
            .map((item) => {
                const sponsoredData = sponsoredByPlatformContentType.value[item.key] || {}
                const budget = sponsoredData.sum_estimated_sponsored_value || 0

                return {
                    key: item.key,
                    label: item.label,
                    platformId: item.platformId,
                    budget,
                }
            })
            .filter((item) => item.budget > 0),
        ['budget'],
        ['desc']
    )

    const totalBudget = rows.reduce((sum, row) => sum + row.budget, 0)
    return rows.map((row) => ({
        ...row,
        budgetPercent: totalBudget > 0 ? (row.budget / totalBudget) * 100 : 0,
    }))
})

// Budget Allocation total for the footer row
const budgetAllocationTotal = computed(() => {
    return budgetAllocationData.value.reduce((sum, row) => sum + row.budget, 0)
})

// Cost Efficiency Analysis data
const costEfficiencyData = computed(() => {
    return _.orderBy(
        platformContentTypeFacets.value
            .map((item) => {
                const sponsoredData = sponsoredByPlatformContentType.value[item.key] || {}
                const budget = sponsoredData.sum_estimated_sponsored_value || 0
                const views = sponsoredData.sum_views || 0
                const cpm = views > 0 ? (budget / views) * 1000 : 0

                return {
                    key: item.key,
                    label: item.label,
                    platformId: item.platformId,
                    budget,
                    views,
                    cpm,
                }
            })
            .filter((item) => item.budget > 0 && item.views > 0),
        ['cpm'],
        ['asc']
    )
})

const costEfficiencyTotals = computed(() => {
    const data = costEfficiencyData.value
    return {
        budget: data.reduce((sum, row) => sum + row.budget, 0),
        views: data.reduce((sum, row) => sum + row.views, 0),
    }
})

const topCreatorsTotals = computed(() => {
    const data = topCreatorsData.value
    return {
        views: data.reduce((sum, c) => sum + c.sponsoredViews, 0),
        payout: data.reduce((sum, c) => sum + c.sponsoredValue, 0),
    }
})

// Top Creators - fetched via API
const topCreatorsData = ref([])
const isLoadingCreators = ref(false)

// Get default avatar URL
const getDefaultAvatar = (creator) => {
    if (creator.primaryPlatformId && creator.primaryPlatformUserId) {
        return `${MEDIA_CDN_BASE}/${creator.primaryPlatformId}/avatars/small/${creator.primaryPlatformUserId}`
    }
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%2394a3b8"%3E%3Cpath d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/%3E%3C/svg%3E'
}

const fetchTopSponsoredCreators = async () => {
    if (!props.reportId || !hasSponsorshipData.value) {
        topCreatorsData.value = []
        return
    }

    isLoadingCreators.value = true
    try {
        // Sort by sponsored value descending - no filter needed, we'll filter client-side
        const response = await axios.post(`/reports/${props.reportId}/items`, {
            paging: {
                currentPage: 1,
                perPage: 50, // Fetch more to filter for sponsored creators
                sort: [
                    { field: 'facets.by_sponsored.true.sum_estimated_sponsored_value', direction: 'desc' }
                ]
            }
        })

        const items = response.data?.payload?.items || []

        // Map and filter for creators with sponsored content, take top 10
        topCreatorsData.value = items
            .map((creator) => {
                const sponsoredFacet = creator.facets?.by_sponsored?.true || {}
                const sponsoredValue = sponsoredFacet.sum_estimated_sponsored_value || 0

                // Skip if no sponsored value
                if (sponsoredValue <= 0) return null

                // Get display name from superviews/partialviews
                const superviews = creator.superviews || []
                const partialviews = creator.partialviews || []
                const allViews = [...superviews, ...partialviews]
                const primaryView = allViews[0]

                const displayName = primaryView?.normalized?.display_name
                    || primaryView?.display_name
                    || creator.display_name
                    || creator.pivot_id
                    || 'Unknown'

                // Get avatar URL
                const avatarUrl = primaryView?.normalized?.avatar_url
                    || primaryView?.avatar_url
                    || null

                // Get primary platform info for default avatar
                const primaryPlatformId = primaryView?.platform_id || creator.platform_ids?.[0] || null
                const primaryPlatformUserId = primaryView?._id || null

                // Get platforms as array for icon display, sorted consistently
                const platformIds = (creator.platform_ids || []).sort((a, b) => {
                    const order = ['youtube', 'twitch', 'tiktok', 'twitter', 'instagram']
                    const indexA = order.indexOf(a)
                    const indexB = order.indexOf(b)
                    // If platform not in order array, put it at the end
                    if (indexA === -1 && indexB === -1) return 0
                    if (indexA === -1) return 1
                    if (indexB === -1) return -1
                    return indexA - indexB
                })

                // Get sponsored views
                const sponsoredViews = sponsoredFacet.sum_views || 0

                // Get performance value
                const performance = creator.facets?.all?.all?.avg_performance ?? null

                return {
                    id: creator.pivot_id || creator.id,
                    displayName,
                    avatarUrl: avatarUrl || getDefaultAvatar({ primaryPlatformId, primaryPlatformUserId }),
                    primaryPlatformId,
                    primaryPlatformUserId,
                    platformIds,
                    sponsoredViews,
                    sponsoredValue,
                    performance,
                }
            })
            .filter(Boolean)
            .filter((c) => {
                if (props.selectedFacetDimension === 'by_platform' && props.selectedFacetKey && props.selectedFacetKey !== 'all') {
                    return c.platformIds.includes(props.selectedFacetKey)
                }
                return true
            })
            .slice(0, 10)

    } catch (error) {
        console.error('Error fetching top sponsored creators:', error)
        topCreatorsData.value = []
    } finally {
        isLoadingCreators.value = false
    }
}

// Fetch creators when reportId, filter selection, or sponsorship data changes
watch(
    () => [props.reportId, hasSponsorshipData.value, props.selectedFacetDimension, props.selectedFacetKey],
    () => {
        if (props.reportId && hasSponsorshipData.value) {
            fetchTopSponsoredCreators()
        }
    },
    { immediate: true }
)

// Get color class for sponsor rate badge
const getSponsorRateClass = (percent) => {
    if (percent >= 10) return 'bg-amber-100 text-amber-700'
    if (percent >= 5) return 'bg-blue-100 text-blue-700'
    return 'bg-slate-100 text-slate-600'
}

// Get budget bar color
const getBudgetBarColor = (key) => {
    return budgetBarColors[key] || 'bg-indigo-400'
}

// Formatting helpers
const formatNumber = (value, format = '0,0') => {
    if (value >= 1_000_000) {
        return numeral(value).format('0.0a').toUpperCase()
    }
    return numeral(value).format(format)
}

const formatPercent = (value) => {
    return numeral(value / 100).format('0.0%')
}

const formatCurrency = (value) => {
    return numeral(value).format('$0,0')
}

const getContentVolumeCsvRows = () => [
    ...contentVolumeData.value.map((row) => ({
        Platform: row.label,
        'Total Views': row.views,
        'Sponsored Views': row.sponsoredViews,
        '% Sponsored': formatPercent(row.sponsoredPercent),
    })),
    {
        Platform: 'Total',
        'Total Views': contentVolumeTotals.value.views,
        'Sponsored Views': contentVolumeTotals.value.sponsoredViews,
        '% Sponsored': formatPercent(contentVolumeTotals.value.sponsoredPercent),
    },
]

const getBudgetAllocationCsvRows = () => [
    ...budgetAllocationData.value.map((row) => ({
        Platform: row.label,
        'Budget (USD)': numeral(row.budget).format('0,0'),
    })),
    {
        Platform: 'Total',
        'Budget (USD)': numeral(budgetAllocationTotal.value).format('0,0'),
    },
]

const getCostEfficiencyCsvRows = () => [
    ...costEfficiencyData.value.map((row) => ({
        Platform: row.label,
        'Budget (USD)': numeral(row.budget).format('0,0'),
        Views: row.views,
        'CPM (USD)': numeral(row.cpm).format('0,0'),
    })),
    {
        Platform: 'Total',
        'Budget (USD)': numeral(costEfficiencyTotals.value.budget).format('0,0'),
        Views: costEfficiencyTotals.value.views,
        'CPM (USD)': '',
    },
]

const getTopCreatorsCsvRows = () => [
    ...topCreatorsData.value.map((creator, index) => ({
        Rank: index + 1,
        Creator: creator.displayName,
        Platforms: getPlatformOrder(creator.platformIds)
            .map((platformId) => platformLabels[platformId] || _.startCase(platformId))
            .join(', '),
        Views: creator.sponsoredViews,
        Performance:
            creator.performance != null
                ? `${creator.performance >= 0 ? '+' : ''}${numeral(creator.performance).format('0.0%')}`
                : '',
        'Est. Payout (USD)': numeral(creator.sponsoredValue).format('0,0'),
    })),
    {
        Rank: '',
        Creator: 'Total',
        Platforms: '',
        Views: topCreatorsTotals.value.views,
        Performance: '',
        'Est. Payout (USD)': numeral(topCreatorsTotals.value.payout).format('0,0'),
    },
]

// Export handlers
const exportContentVolume = async () => {
    if (contentVolumeRef.value) {
        await downloadAsImage(contentVolumeRef.value, 'content-volume-distribution')
    }
}

const exportBudgetAllocation = async () => {
    if (budgetAllocationRef.value) {
        await downloadAsImage(budgetAllocationRef.value, 'budget-allocation')
    }
}

const exportCostEfficiency = async () => {
    if (costEfficiencyRef.value) {
        await downloadAsImage(costEfficiencyRef.value, 'cost-efficiency')
    }
}

const exportTopCreators = async () => {
    if (topCreatorsRef.value) {
        // Find the scrollable container and temporarily remove max-height for export
        const scrollableContainer = topCreatorsRef.value.querySelector('.export-full-list')
        if (scrollableContainer) {
            const originalMaxHeight = scrollableContainer.style.maxHeight
            const originalOverflow = scrollableContainer.style.overflow
            // Remove max-height and overflow restrictions for full export
            scrollableContainer.style.maxHeight = 'none'
            scrollableContainer.style.overflow = 'visible'

            try {
                await downloadAsImage(topCreatorsRef.value, 'top-creators-hired')
            } finally {
                // Restore original styles
                scrollableContainer.style.maxHeight = originalMaxHeight
                scrollableContainer.style.overflow = originalOverflow
            }
        } else {
            await downloadAsImage(topCreatorsRef.value, 'top-creators-hired')
        }
    }
}

const exportContentVolumeCsv = async () => {
    await downloadAsCsv(getContentVolumeCsvRows(), 'content-volume')
}

const exportBudgetAllocationCsv = async () => {
    await downloadAsCsv(getBudgetAllocationCsvRows(), 'budget-allocation')
}

const exportCostEfficiencyCsv = async () => {
    await downloadAsCsv(getCostEfficiencyCsvRows(), 'cost-efficiency')
}

const exportTopCreatorsCsv = async () => {
    await downloadAsCsv(getTopCreatorsCsvRows(), 'top-sponsored-creators')
}
</script>
